import type { ComponentProps } from 'preact';
import { Button } from '../../../base/Button/Button';
import { Heading } from '../../../base/Heading/Heading';
import type { TimetableDetailsLine } from '../../../../data/timetableDetailsLine';
import styles from './TimetableHeader.module.css';
import { SvgIcon } from '../../../base/Icon/SvgIcon';

export interface TimetableHeaderProps extends Omit<
  ComponentProps<'header'>,
  'children' | 'className'
> {
  line: TimetableDetailsLine;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export function TimetableHeader({
  line,
  expanded = false,
  onToggleExpand,
  style,
  ...props
}: TimetableHeaderProps) {
  const title = line.title || line.name;
  const backgroundColor =
    line.colors?.background ?? 'var(--widget-brand-secondary)';
  const foregroundColor = line.colors?.foreground ?? '#FFFFFF';
  const styleOverrides =
    style && typeof style === 'object' && !Array.isArray(style) ? style : {};

  return (
    <header
      {...props}
      className={styles.header}
      style={{
        backgroundColor,
        color: foregroundColor,
        ...styleOverrides,
      }}
    >
      <div className={styles.content}>
        <div className={styles.textWrap}>
          <div className={styles.titleWrapper}>
            <Heading as="h3" variant="md" className={styles.title}>
              {title}
            </Heading>
            {line.description && line.description !== title && (
              <p className={styles.description}>{line.description}</p>
            )}
          </div>
          {onToggleExpand && (
            <Button
              type="button"
              variant="primary"
              aria-expanded={expanded}
              onClick={onToggleExpand}
              style={{
                backgroundColor: foregroundColor,
                color: backgroundColor,
                borderColor: foregroundColor,
              }}
            >
              <div className={styles.expandWrapper}>
                <SvgIcon
                  size={20}
                  fill="currentColor"
                  strokeWidth="0.5"
                  name="expand"
                />
                {expanded ? 'Close' : 'Expand'}
              </div>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
